<template>
  <div class="pa-4">
    <div class="d-flex justify-space-between align-center mb-4">
      <h2>Contact Categorieën</h2>
      <v-btn color="primary" prepend-icon="mdi-plus" @click="openCreateDialog">
        Categorie Toevoegen
      </v-btn>
    </div>

    <!-- Loading State -->
    <v-progress-linear v-if="store.loading" indeterminate></v-progress-linear>

    <!-- Error State -->
    <v-alert v-if="store.error" type="error" class="mb-4">
      {{ store.error }}
    </v-alert>

    <!-- Empty State -->
    <v-card
      v-if="!store.loading && store.categories.length === 0"
      class="pa-8 text-center"
    >
      <v-icon size="64" color="grey-lighten-1" class="mb-4"
        >mdi-folder-outline</v-icon
      >
      <h3 class="text-h6 mb-2">Geen Categorieën</h3>
      <p class="text-body-2 text-medium-emphasis mb-4">
        Begin door je eerste contact categorie te maken.
      </p>
      <v-btn color="primary" prepend-icon="mdi-plus" @click="openCreateDialog">
        Categorie Toevoegen
      </v-btn>
    </v-card>

    <!-- Categories List -->
    <v-card v-if="!store.loading && store.categories.length > 0">
      <v-list>
        <v-list-item
          v-for="category in store.categories"
          :key="category.id"
          class="border-b"
        >
          <template #prepend>
            <v-icon :color="category.color || 'primary'">{{
              category.icon || "mdi-folder"
            }}</v-icon>
          </template>

          <div class="d-flex flex-column">
            <v-list-item-title>
              {{ category.name }}
              <v-chip
                v-if="!category.active"
                size="small"
                color="warning"
                variant="outlined"
                class="ml-2"
              >
                Inactief
              </v-chip>
            </v-list-item-title>
            <v-list-item-subtitle>
              {{ category.contacts_count || 0 }} contact(en)
            </v-list-item-subtitle>
          </div>

          <template #append>
            <div class="d-flex ga-2">
              <v-btn
                icon="mdi-pencil"
                size="small"
                variant="text"
                @click="openEditDialog(category)"
              ></v-btn>
              <v-btn
                icon="mdi-delete"
                size="small"
                variant="text"
                color="error"
                :disabled="category.contacts_count > 0"
                :title="
                  category.contacts_count > 0
                    ? 'Kan niet verwijderen: categorie is in gebruik'
                    : 'Verwijderen'
                "
                @click="confirmDelete(category)"
              ></v-btn>
            </div>
          </template>
        </v-list-item>
      </v-list>
    </v-card>

    <!-- Create/Edit Dialog -->
    <v-dialog v-model="dialog" max-width="500px" persistent>
      <v-card>
        <v-card-title>
          {{ isEditing ? "Categorie Bewerken" : "Categorie Toevoegen" }}
        </v-card-title>

        <v-card-text>
          <v-form ref="form" v-model="formValid">
            <v-text-field
              v-model="formData.name"
              label="Naam"
              :rules="nameRules"
              required
              autofocus
            ></v-text-field>

            <v-switch
              v-model="formData.active"
              label="Actief"
              color="primary"
              hide-details
              class="mb-4"
            ></v-switch>

            <h4 class="text-subtitle-1 mb-3">Weergave</h4>

            <ColorPickerField
              v-model="formData.color"
              label="Kleur"
              hint="Laat leeg voor standaard kleur"
            />

            <IconPickerField
              v-model="formData.icon"
              label="Icoon"
              :preview-color="formData.color || 'primary'"
              placeholder="Bijv: mdi-folder, mdi-account-group"
            />

            <!-- Preview -->
            <div
              v-if="formData.name && (formData.icon || formData.color)"
              class="mb-4"
            >
              <div class="text-caption mb-2">Voorbeeld:</div>
              <v-chip variant="tonal" label>
                <template #prepend>
                  <v-icon
                    :color="formData.color"
                    size="small"
                    class="category-icon"
                  >
                    {{ formData.icon || "mdi-folder" }}
                  </v-icon>
                </template>
                {{ formData.name }}
              </v-chip>
            </div>
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="closeDialog">Annuleren</v-btn>
          <v-btn
            color="primary"
            :loading="saving"
            :disabled="!formValid"
            @click="save"
          >
            {{ isEditing ? "Bijwerken" : "Maken" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400px">
      <v-card>
        <v-card-title>Verwijdering Bevestigen</v-card-title>
        <v-card-text>
          Weet je zeker dat je "{{ categoryToDelete?.name }}" wilt verwijderen?
          Deze actie kan niet ongedaan worden gemaakt.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="deleteDialog = false">Annuleren</v-btn>
          <v-btn color="error" :loading="deleting" @click="deleteCategory">
            Verwijderen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from "vue";
import { useContactCategoriesStoreRefactor } from "@/stores/contactCategoriesStoreRefactor";
import ColorPickerField from "@/components/ui/ColorPickerField.vue";
import IconPickerField from "@/components/ui/IconPickerField.vue";

const store = useContactCategoriesStoreRefactor();

// Data
const dialog = ref(false);
const deleteDialog = ref(false);
const formValid = ref(false);
const saving = ref(false);
const deleting = ref(false);
const isEditing = ref(false);
const categoryToDelete = ref(null);

const formData = reactive({
  id: null,
  name: "",
  active: true,
  icon: "",
  color: "",
});

// Form validation rules
const nameRules = [
  (v) => !!v || "Naam is verplicht",
  (v) => (v && v.length <= 255) || "Naam moet minder dan 255 karakters zijn",
];

// Methods
const openCreateDialog = () => {
  isEditing.value = false;
  formData.id = null;
  formData.name = "";
  formData.active = true;
  formData.icon = "";
  formData.color = "";
  dialog.value = true;
};

const openEditDialog = (category) => {
  isEditing.value = true;
  formData.id = category.id;
  formData.name = category.name;
  formData.active = category.active ?? true;
  formData.icon = category.icon || "";
  formData.color = category.color || "";
  dialog.value = true;
};

const closeDialog = () => {
  dialog.value = false;
};

const save = async () => {
  if (!formValid.value) return;

  saving.value = true;

  try {
    const categoryData = {
      name: formData.name,
      active: formData.active,
      icon: formData.icon || null,
      color: formData.color || null,
    };

    if (isEditing.value) {
      categoryData.id = formData.id;
      await store.update(categoryData);
    } else {
      await store.create(categoryData);
    }

    closeDialog();
  } catch (error) {
    console.error("Error saving category:", error);
  } finally {
    saving.value = false;
  }
};

const confirmDelete = (category) => {
  categoryToDelete.value = category;
  deleteDialog.value = true;
};

const deleteCategory = async () => {
  if (!categoryToDelete.value) return;

  deleting.value = true;

  try {
    await store.destroy(categoryToDelete.value.id);
    deleteDialog.value = false;
    categoryToDelete.value = null;
  } catch (error) {
    console.error("Error deleting category:", error);
  } finally {
    deleting.value = false;
  }
};

// Load data on mount
onMounted(async () => {
  await store.fetchAll();
});
</script>

<style scoped>
.category-icon {
  margin-right: 4px !important;
}
</style>
